import React, { useEffect, useState, useMemo } from "react";
import useCareLabels from "hooks/useCareLabels";
import { useSelector } from "react-redux";
import ActivityStat from "../../../Pages/ServiceProvider/ActivityStat/ActivityStat";
import FormsList from "../../../Pages/ServiceProvider/FormsList/FormsList";
import ProfileDetails from "../../../Pages/ServiceProvider/ProfileDetails/ProfileDetails";
import CircularLoader from "../../../common/CircularLoader/CircularLoader";
import SubmittedFormView from "../../SubmittedFormsView/index";
import Appointments from "../Appointments/index";
import Settings from "../Setting/index";
import {
  PARENT_FORM_CATEGORY_SLUG,
  PARTICIPANT_PROFILE_TABS,
  USER_ROLE,
} from "../../../../Constants/constant";
import ParticipantProfileTabItem from "./ParticipantProfileTabItem";
import RiskManagementTab from "./RiskManagementTab";
import Documents from "../Documents/index";
import { parseBooleanVal, selectedTabState } from "../../../../Utils/utils";
import CaseNotesTab from "../CaseNotes";
import FundingSetup from "../../../../pages/SupportCoordination/Funding/FundingSetup/index";
import { roleAccess, hasShiftNotesAccess, canViewAuditLogActivity } from "../../../../helpers/RolesPermission";
import { getSpRosteringSetting } from "store/selector/sp.selector";
import { PERMISSION_KEYS, PERMISSION_KEYS_CHILDREN } from "store/constants";
import MedicationApplicationRecord from "../MAR/MedicationList";
import MedicationHistory from "../MAR/MedicationHistory";
import ParticipantTasks from "../ParticipantTasks/ParticipantTasks";
import ShiftNotes from "../ShiftNotes/index";
import { ActivityTab } from "components/common/AuditLog";
import { trackTabSwitch } from "Utils/trackTabSwitch";
import { apiGetRequest } from "helpers/Requests";
import { getUrlRole } from "Utils/utils";
import { extractFormCategoriesFromResponse } from "components/DynamicForms/shared/formCategoryUtils";
import {
  isCustomCrmFormCategoryTabId,
  mergeCustomCrmCategoriesIntoFormsTab,
} from "./participantProfileCrmCategoryTabs.utils";
import {
  buildParticipantProfileFormsTabChildren,
  setParticipantProfileFormsSubTabs,
} from "./participantProfileFormsTab.utils";
import "./ParticipantProfileTabs.css";

const mapParticipantTabLabels = (tabs, participantLabel) =>
  tabs.map((tab) => ({
    ...tab,
    name: tab.name?.includes("Participant")
      ? tab.name.replace(/Participant/g, participantLabel)
      : tab.name,
    children: tab.children?.map((child) => ({
      ...child,
      name: child.name?.includes("Participant")
        ? child.name.replace(/Participant/g, participantLabel)
        : child.name,
    })),
  }));

const ParticipantProfileTabs = ({ ...props }) => {
  const { participantLabel } = useCareLabels();
  const spRosteringSetting = useSelector(getSpRosteringSetting);
  const {
    getFolderListForHRMLoading,
    FolderListForHRM,
    FileListForHRM,
    participantInfo,
    isCaseNoteCall,
    resetCaseNoteCall,
    toggleState,
    setToggleState,
    refreshNavigations,
    moduleType,
  } = props;

  const tabs = useMemo(
    () => mapParticipantTabLabels(PARTICIPANT_PROFILE_TABS, participantLabel),
    [participantLabel]
  );

  const [selectedTab] = useState(
    selectedTabState.get()
      ? selectedTabState.get()
      : PARTICIPANT_PROFILE_TABS[0]
  );
  const [crmFormCategoriesFromApi, setCrmFormCategoriesFromApi] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const loadFormCategories = async () => {
      const prefixes = roleAccess([USER_ROLE.Sp_Standard_User])
        ? ["worker", "hrm"]
        : [getUrlRole() || "sp"];

      let categories = [];
      for (const rolePrefix of prefixes) {
        try {
          const res = await apiGetRequest(`${rolePrefix}/form-categories`);
          const payload = res?.data?.data ?? res?.data ?? res;
          const list = extractFormCategoriesFromResponse(payload);
          if (list.length > 0) {
            categories = list;
            break;
          }
        } catch {
          /* try next prefix */
        }
      }

      if (cancelled) {
        return;
      }

      setCrmFormCategoriesFromApi(categories);
    };
    loadFormCategories();
    return () => {
      cancelled = true;
    };
  }, [participantInfo?.uuid]);

  const toggleTab = (tab) => {
    const fromTab = toggleState?.name || toggleState?.id;
    const toTab = tab?.name || tab?.id;
    setToggleState(tab);
    selectedTabState.save(JSON.stringify(tab));
    try {
      trackTabSwitch("participant", fromTab, toTab);
    } catch (e) {}
  };

  useEffect(() => {
    if (toggleState.id !== selectedTab.id) setToggleState(selectedTab);
  }, [selectedTab]);

  const loadTabComponent = () => {
    if (
      isCustomCrmFormCategoryTabId(toggleState.id) &&
      toggleState.categoryUuid &&
      participantInfo?.uuid
    ) {
      return (
        <SubmittedFormView
          supportPlansId={toggleState.categoryUuid}
          participantsUid={participantInfo.uuid}
          title={toggleState.name}
          participantId={participantInfo.id}
          parentSlug={toggleState.slug}
        />
      );
    }

    switch (toggleState.id) {
      case 1:
        return (
          <ProfileDetails
            participantInfo={participantInfo}
            setToggleState={setToggleState}
          />
        );
      case 2:
        return (
          <>
            <ActivityStat />
            <FormsList background='bg-gradient' textColor='text-gray-50' />
            <FormsList background='bg-white' textColor='text-gray-900' />
          </>
        );
      case 5:
        return (
          <>
            {participantInfo && participantInfo.uuid ? (
              <SubmittedFormView
                parentSlug={PARENT_FORM_CATEGORY_SLUG.MEDICATION.SLUG}
                participantId={participantInfo.id}
                supportPlansId={PARENT_FORM_CATEGORY_SLUG.MEDICATION.UUID}
                participantsUid={props.participantInfo.uuid}
                title='Medication'
              />
            ) : (
              <CircularLoader
                classes='flex justify-center items-center'
                classloader='loader-l'
              />
            )}
          </>
        );
      case 6:
        return (
          <>
            {participantInfo.uuid && (
              <SubmittedFormView
                parentSlug={
                  PARENT_FORM_CATEGORY_SLUG.PARTICIPANT_ASSESSMENT.SLUG
                }
                participantId={participantInfo.id}
                supportPlansId={
                  PARENT_FORM_CATEGORY_SLUG.PARTICIPANT_ASSESSMENT.UUID
                }
                participantsUid={props.participantInfo.uuid}
                title={PARENT_FORM_CATEGORY_SLUG.PARTICIPANT_ASSESSMENT.NAME.replace(
                  /Participant/g,
                  participantLabel
                )}
              />
            )}
          </>
        );
      case 7:
        return (
          <>
            {participantInfo.uuid && (
              <SubmittedFormView
                participantId={participantInfo.id}
                supportPlansId={PARENT_FORM_CATEGORY_SLUG.RISK_MANAGEMENT.UUID}
                participantsUid={props.participantInfo.uuid}
                title={PARENT_FORM_CATEGORY_SLUG.RISK_MANAGEMENT.NAME}
                parentSlug={PARENT_FORM_CATEGORY_SLUG.RISK_MANAGEMENT.SLUG}
              />
            )}
          </>
        );
      case 8:
        return (
          <>
            {participantInfo.uuid && (
              <SubmittedFormView
                participantId={participantInfo.id}
                supportPlansId={PARENT_FORM_CATEGORY_SLUG.HIGH_CARE.UUID}
                participantsUid={props.participantInfo.uuid}
                title='High Care'
                parentSlug={PARENT_FORM_CATEGORY_SLUG.HIGH_CARE.SLUG}
              />
            )}
          </>
        );
      case 9:
        return (
          <>
            {participantInfo.uuid && (
              <SubmittedFormView
                participantId={participantInfo.id}
                supportPlansId={
                  PARENT_FORM_CATEGORY_SLUG.INJURY_MANAGEMENT.UUID
                }
                participantsUid={props.participantInfo.uuid}
                title={PARENT_FORM_CATEGORY_SLUG.INJURY_MANAGEMENT.NAME}
                parentSlug={PARENT_FORM_CATEGORY_SLUG.INJURY_MANAGEMENT.SLUG}
              />
            )}
          </>
        );
      case 10:
        return (
          roleAccess(
            [],
            PERMISSION_KEYS.PARTICIPANT,
            PERMISSION_KEYS_CHILDREN.PARTICIPANT.DOCUMEMT.SHOW
          ) && (
            <Documents participantInfo={participantInfo} />
          )
        );
      case 11:
        return (
          <RiskManagementTab
            participantId={participantInfo.id}
            participantInfoUUID={participantInfo.uuid || ""}
            parentSlug={PARENT_FORM_CATEGORY_SLUG.RISK_MANAGEMENT.SLUG}
          />
        );
      case 12:
        return (
          <Appointments type='page_render' participantInfo={participantInfo} />
        );
      case 13:
        return (
          roleAccess(
            [],
            PERMISSION_KEYS.PARTICIPANT,
            PERMISSION_KEYS_CHILDREN.PARTICIPANT.SETTING.SHOW
          ) && <Settings />
        );
      case 14:
        return (
          <>
            {participantInfo && participantInfo.uuid ? (
              <SubmittedFormView
                participantId={participantInfo.id}
                supportPlansId={
                  PARENT_FORM_CATEGORY_SLUG.PERSON_CENTERED_PLAN.UUID
                }
                participantsUid={props.participantInfo.uuid}
                title={PARENT_FORM_CATEGORY_SLUG.PERSON_CENTERED_PLAN.NAME}
                parentSlug={PARENT_FORM_CATEGORY_SLUG.PERSON_CENTERED_PLAN.SLUG}
              />
            ) : (
              <CircularLoader
                classes='flex justify-center items-center'
                classloader='loader-l'
              />
            )}
          </>
        );
      case 15:
        return (
          <>
            {participantInfo.uuid && (
              <SubmittedFormView
                supportPlansId={PARENT_FORM_CATEGORY_SLUG.SUPPORT_PLAN.UUID}
                participantsUid={props.participantInfo.uuid}
                title='Support Plan'
                participantId={participantInfo.id}
                parentSlug={PARENT_FORM_CATEGORY_SLUG.SUPPORT_PLAN.SLUG}
              />
            )}
          </>
        );
      case 16:
        return (
          <>
            {participantInfo.uuid && (
              <CaseNotesTab
                participantInfo={participantInfo}
                isCaseNoteCall={isCaseNoteCall}
                resetCaseNoteCall={resetCaseNoteCall}
                setToggleState={setToggleState}
              />
            )}
          </>
        );
      case 17:
        return (
          <>
            {roleAccess(
              [],
              PERMISSION_KEYS.PARTICIPANT,
              PERMISSION_KEYS_CHILDREN.PARTICIPANT.FUNDING.SHOW
            ) &&
              participantInfo.uuid && (
                <FundingSetup type='tab' setToggleState={setToggleState} moduleType={moduleType} />
              )}
          </>
        );
      case 18:
        return (
          <>
            {participantInfo.uuid && (
              <SubmittedFormView
                supportPlansId={PARENT_FORM_CATEGORY_SLUG.Exit_Transaction.UUID}
                participantsUid={props.participantInfo.uuid}
                title='Exit/Transition'
                participantId={participantInfo.id}
                parentSlug={PARENT_FORM_CATEGORY_SLUG.Exit_Transaction.SLUG}
              />
            )}
          </>
        );
      case 25:
        return (
          <>
            {participantInfo.uuid && (
              <SubmittedFormView
                supportPlansId={PARENT_FORM_CATEGORY_SLUG.Other.UUID}
                participantsUid={props.participantInfo.uuid}
                title='Other'
                participantId={participantInfo.id}
                parentSlug={PARENT_FORM_CATEGORY_SLUG.Other.SLUG}
              />
            )}
          </>
        );
      case 19:
        return (
          <>
            {(hasShiftNotesAccess() ||
              roleAccess([USER_ROLE.Sp_Admin, USER_ROLE.Sp_manager])) &&
              participantInfo.uuid && (
                <ShiftNotes participantInfo={participantInfo} />
              )}
          </>
        );
      case 21:
        return (
          <MedicationApplicationRecord participantInfo={participantInfo} />
        );
      case 22:
        return (
          <MedicationHistory participantInfo={participantInfo} />
        );
      case 23:
        return (
          <>
            {participantInfo.uuid && (
              <ParticipantTasks participantInfo={participantInfo} />
            )}
          </>
        );
      case 24:
        return (
          <ActivityTab entityType="participant" entityId={participantInfo.id} />
        );

      default:
        return <></>;
    }
  };
  const tabsToMap = useMemo(() => {
    const filterTabs = (tab) => {
      if (tab.id === 24 && !canViewAuditLogActivity(spRosteringSetting)) {
        return false;
      }
      const permissions = tab.permissions;
      // Shift Notes (id 19): permission-based for Worker (sp_standard_user); role-based for Manager/Admin so tab shows on Manager Portal
      if (tab.id === 19 && permissions) {
        return (
          hasShiftNotesAccess() ||
          roleAccess([USER_ROLE.Sp_Admin, USER_ROLE.Sp_manager])
        );
      }
      return (
        !permissions ||
        roleAccess([], PERMISSION_KEYS.PARTICIPANT, permissions.SHOW)
      );
    };
    const filterChildTabs = (childTab) => {
      const permissions = childTab.permissions;
      return (
        !permissions || roleAccess([], PERMISSION_KEYS.PARTICIPANT, permissions)
      );
    };
    const filteredTabs = tabs.filter(filterTabs);
    filteredTabs.forEach((tab) => {
      const childs = tab.children;
      if (childs && childs.length > 0) {
        tab.children = childs.filter(filterChildTabs);
      }
    });
    const finalTabs = filteredTabs?.filter((x) => roleAccess([...x.roleAccess]));

    const formsChildrenFromApi =
      buildParticipantProfileFormsTabChildren(crmFormCategoriesFromApi);

    if (formsChildrenFromApi.length > 0) {
      return finalTabs.map((tab) =>
        tab.id === 4 ? { ...tab, children: formsChildrenFromApi } : tab
      );
    }

    return mergeCustomCrmCategoriesIntoFormsTab(finalTabs, crmFormCategoriesFromApi);
  }, [
    tabs,
    participantInfo,
    refreshNavigations,
    spRosteringSetting,
    crmFormCategoriesFromApi,
  ]);

  useEffect(() => {
    const formsTab = tabsToMap.find((tab) => tab.id === 4);
    setParticipantProfileFormsSubTabs(formsTab?.children ?? []);
  }, [tabsToMap]);

  return (
    <div className='bg-white'>
      {/* Tab Navigation - All tabs in one row */}
      <div
        className='print:hidden sticky z-[20] bg-gradient border-b border-blue-600/20 shadow-sm participant-tabs-container'
        style={{ top: "var(--profile-header-h, 0px)" }}
      >
        <div className='participant-tabs-wrapper overflow-hidden'>
          <div className='flex items-center participant-tabs-flex'>
            {tabsToMap.map((tab, index) => (
              <ParticipantProfileTabItem
                tabItem={tab}
                key={index}
                toggleTab={toggleTab}
                selectedTab={toggleState.id}
              />
            ))}
          </div>
        </div>
      </div>
      
      {/* Content Area */}
      <div className='content-tabs'>
        <div className={"content active-content p-4 sm:p-6 lg:p-8 bg-gray-50 min-h-screen"}>
          {loadTabComponent()}
        </div>
      </div>
    </div>
  );
};

export default ParticipantProfileTabs;
